import { RetailerListElementResponseType } from "@sellar-io/api-v2/types";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { StepType } from "../types";

export type BuyerCreationFlowSteps =
  | "BuyerFindCompanyStep"
  | "BuyerBusinessMatchStep"
  | "BuyerBusinessSearchStep"
  | "BuyerBusinessFormStep"
  | "BuyerBusinessClaimed"
  | "BuyerPostcodeStep"
  | "BuyerClaimMismatch"
  | "BuyerBusinessConnectStep";

export interface BuyerCreationFlowStepType extends StepType {
  name: BuyerCreationFlowSteps;
  backStep?: BuyerCreationFlowSteps;
}

type BuyerCreationFlowStoreState = {
  step?: BuyerCreationFlowStepType;
  buyerSelectBusinessId: number | undefined;
  buyerDetails: RetailerListElementResponseType | undefined;
  postcode: string | undefined;
  searchGooglePlacesResult: google.maps.places.PlaceResult | undefined;
};

type BuyerCreationFlowStoreActions = {
  setStep: (step: BuyerCreationFlowStepType) => void;
  setBuyerSelectBusinessId: (buyerSelectBusinessId: number | undefined) => void;
  setBuyerDetails: (
    buyerDetails: RetailerListElementResponseType | undefined,
  ) => void;
  setPostcode: (postcode: string | undefined) => void;
  setSearchGooglePlacesResult: (
    results: google.maps.places.PlaceResult | undefined,
  ) => void;
  reset: () => void;
};

export const BuyerCreationFlowStep: {
  BuyerFindCompanyStep: BuyerCreationFlowStepType;
  BuyerBusinessSearchStep: BuyerCreationFlowStepType;
  BuyerBusinessFormStep: BuyerCreationFlowStepType;
  BuyerBusinessMatchStep: BuyerCreationFlowStepType;
  BuyerPostcodeStep: BuyerCreationFlowStepType;
  BuyerBusinessClaimed: BuyerCreationFlowStepType;
  BuyerClaimMismatch: BuyerCreationFlowStepType;
  BuyerBusinessConnectStep: BuyerCreationFlowStepType;
} = {
  BuyerFindCompanyStep: {
    name: "BuyerFindCompanyStep",
  },
  BuyerBusinessMatchStep: {
    name: "BuyerBusinessMatchStep",
    backStep: "BuyerFindCompanyStep",
  },
  BuyerBusinessSearchStep: {
    name: "BuyerBusinessSearchStep",
    backStep: "BuyerFindCompanyStep",
  },
  BuyerBusinessFormStep: {
    name: "BuyerBusinessFormStep",
    backStep: "BuyerFindCompanyStep",
  },
  BuyerBusinessClaimed: {
    name: "BuyerBusinessClaimed",
    backStep: "BuyerFindCompanyStep",
  },
  BuyerPostcodeStep: {
    name: "BuyerPostcodeStep",
    backStep: "BuyerFindCompanyStep",
  },
  BuyerClaimMismatch: {
    name: "BuyerClaimMismatch",
    backStep: "BuyerFindCompanyStep",
  },
  BuyerBusinessConnectStep: {
    name: "BuyerBusinessConnectStep",
  },
};

const initialBuyerCreationFlowStoreState: BuyerCreationFlowStoreState = {
  postcode: undefined,
  searchGooglePlacesResult: undefined,
  buyerSelectBusinessId: undefined,
  buyerDetails: undefined,
  step: BuyerCreationFlowStep.BuyerFindCompanyStep,
};

type LegacyBuyerCreationFlowStepName =
  | "RetailerFindCompanyStep"
  | "RetailerBusinessMatchStep"
  | "RetailerBusinessSearchStep"
  | "RetailerBusinessFormStep"
  | "RetailerBusinessClaimed"
  | "RetailerPostcodeStep"
  | "RetailerClaimMismatch"
  | "RetailerBusinessConnectStep";

type PersistedBuyerCreationFlowState = {
  buyerSelectBusinessId?: number;
  buyerDetails?: RetailerListElementResponseType;
  retailerSelectBusinessId?: number;
  retailerDetails?: RetailerListElementResponseType;
  postcode?: string;
  searchGooglePlacesResult?: google.maps.places.PlaceResult;
  step?: {
    name: BuyerCreationFlowSteps | LegacyBuyerCreationFlowStepName;
    backStep?: BuyerCreationFlowSteps | LegacyBuyerCreationFlowStepName;
  };
};

const legacyStepNameMap: Record<
  LegacyBuyerCreationFlowStepName,
  BuyerCreationFlowSteps
> = {
  RetailerFindCompanyStep: "BuyerFindCompanyStep",
  RetailerBusinessMatchStep: "BuyerBusinessMatchStep",
  RetailerBusinessSearchStep: "BuyerBusinessSearchStep",
  RetailerBusinessFormStep: "BuyerBusinessFormStep",
  RetailerBusinessClaimed: "BuyerBusinessClaimed",
  RetailerPostcodeStep: "BuyerPostcodeStep",
  RetailerClaimMismatch: "BuyerClaimMismatch",
  RetailerBusinessConnectStep: "BuyerBusinessConnectStep",
};

const migrateStepName = (
  name: BuyerCreationFlowSteps | LegacyBuyerCreationFlowStepName,
): BuyerCreationFlowSteps => {
  return name in legacyStepNameMap
    ? legacyStepNameMap[name as LegacyBuyerCreationFlowStepName]
    : (name as BuyerCreationFlowSteps);
};

const migrateBuyerCreationFlowState = (
  persistedState: unknown,
): PersistedBuyerCreationFlowState => {
  const state =
    typeof persistedState === "object" && persistedState !== null
      ? (persistedState as PersistedBuyerCreationFlowState)
      : {};

  return {
    ...state,
    buyerSelectBusinessId:
      state.buyerSelectBusinessId ?? state.retailerSelectBusinessId,
    buyerDetails: state.buyerDetails ?? state.retailerDetails,
    step: state.step
      ? {
          ...state.step,
          name: migrateStepName(state.step.name),
          backStep: state.step.backStep
            ? migrateStepName(state.step.backStep)
            : undefined,
        }
      : undefined,
  };
};

const key = "retailerCreationFlowStore";
export const useBuyerCreationFlowStore = create<
  BuyerCreationFlowStoreState & BuyerCreationFlowStoreActions
>()(
  persist(
    (set) => {
      return {
        ...initialBuyerCreationFlowStoreState,
        setStep: (step) => set({ step }),
        setBuyerSelectBusinessId: (buyerSelectBusinessId) =>
          set({ buyerSelectBusinessId }),
        setBuyerDetails: (buyerDetails) => set({ buyerDetails }),
        setPostcode: (postcode) => set({ postcode }),
        setSearchGooglePlacesResult: (searchGooglePlacesResult) =>
          set({ searchGooglePlacesResult }),
        reset: () => set(initialBuyerCreationFlowStoreState),
      };
    },
    {
      name: key,
      partialize: (state) => ({
        postcode: state.postcode,
        searchGooglePlacesResult: state.searchGooglePlacesResult,
        buyerSelectBusinessId: state.buyerSelectBusinessId,
        buyerDetails: state.buyerDetails,
        step: state.step,
      }),
      version: 1,
      migrate: migrateBuyerCreationFlowState,
    },
  ),
);
